import { gql, TypedDocumentNode } from "@apollo/client";

import {
  PropertiesQueryQuery,
  PropertiesQueryQueryVariables,
} from "src/graphql/anti-fraud/__generated__/graphql";
import { useFetchQuery } from "src/graphql/utils";
import { mapDataTypeToAppDataType } from "src/mappers/typedValue.mappers";
import { ClientSchemaProperty } from "src/types/clientSchema";
import { isEmpty } from "src/utils/arrays.utils.ts";

const propertiesQuery = gql(/* GraphQL */ `
  query PropertiesQuery {
    schema {
      caseTypes {
        code
        name
        properties {
          name
          datatype
          mandatory
          code
        }
        elements {
          elementType {
            code
            name
            properties {
              name
              datatype
              mandatory
              code
            }
          }
        }
      }
    }
  }
`) as TypedDocumentNode<PropertiesQueryQuery, PropertiesQueryQueryVariables>;

export const useGetPropertiesQuery = ({
  onError,
  caseTypeCode,
}: {
  onError: (error: Error) => void;
  caseTypeCode?: string;
}) => {
  return useFetchQuery(propertiesQuery, {
    toData: (data) =>
      data?.schema?.caseTypes
        ?.filter((caseType) => !caseTypeCode || caseType.code === caseTypeCode)
        ?.flatMap((caseType) => [
          // Map case type properties
          ...caseType.properties.map(
            (property) =>
              ({
                propertyCode: property.code,
                propertyName: property.name,
                dataType: mapDataTypeToAppDataType(property.datatype),
                mandatory: property.mandatory,
                elementCode: null,
                prefix: caseType.name,
              } satisfies ClientSchemaProperty)
          ),
          // Map element properties
          ...caseType.elements.flatMap((element) =>
            element.elementType.properties.map(
              (property) =>
                ({
                  propertyCode: property.code,
                  propertyName: property.name,
                  dataType: mapDataTypeToAppDataType(property.datatype),
                  mandatory: property.mandatory,
                  elementCode: element.elementType.code,
                  prefix: element.elementType.name,
                } satisfies ClientSchemaProperty)
            )
          ),
        ]) ?? [],
    isEmpty: (data) => isEmpty(data?.schema?.caseTypes),
    queryOptions: {
      onError,
    },
    uiErrors: {
      fetchError: "FETCH-IDENTIFIER-TYPES-ERROR",
      emptyError: "EMPTY-IDENTIFIER-TYPES-ERROR",
    },
  });
};
